import { createSlice } from '@reduxjs/toolkit';
import { get, has, reduce, omit } from 'lodash';

import { API } from 'store/middleware/api';
import { unitSchema } from 'store/schema/units';
import { facilitySchema } from 'store/schema/facilities';
import formatFacility from 'utils/store/formatFacility';
import { formatFacilityResponse } from 'utils/store/formatSearchFacilities';
import { mergeFacilities } from 'utils/store/mergeEntities';

import { SEARCH } from 'store/search/actions';
import { PAGES } from 'store/pages/actions';
import { REVIEWS } from 'store/reviews/actions';
import { UNITS } from 'store/units/actions';
import { BOOKINGS } from 'store/bookings/actions';

export const FACILITIES = {
	REQUEST: 'SFDC/FACILITIES/REQUEST',
	SUCCESS: 'SFDC/FACILITIES/SUCCESS',
	FAILURE: 'SFDC/FACILITIES/FAILURE',
	BY_ID_REQUEST: 'SFDC/FACILITIES/BY_ID_REQUEST',
	BY_ID_SUCCESS: 'SFDC/FACILITIES/BY_ID_SUCCESS',
	BY_ID_FAILURE: 'SFDC/FACILITIES/BY_ID_FAILURE',
	BY_EMAIL_TOKEN_REQUEST: 'SFDC/FACILITIES/BY_EMAIL_TOKEN_REQUEST',
	BY_EMAIL_TOKEN_SUCCESS: 'SFDC/FACILITIES/BY_EMAIL_TOKEN_SUCCESS',
	BY_EMAIL_TOKEN_FAILURE: 'SFDC/FACILITIES/BY_EMAIL_TOKEN_FAILURE',
	FACILITY_PAGE_REQUEST: 'SFDC/FACILITIES/FACILITY_PAGE_REQUEST',
	FACILITY_PAGE_SUCCESS: 'SFDC/FACILITIES/FACILITY_PAGE_SUCCESS',
	FACILITY_PAGE_FAILURE: 'SFDC/FACILITIES/FACILITY_PAGE_FAILURE',
	FACILITY_HOURS_DIRECTION_REQUEST: 'SFDC/FACILITIES/FACILITY_HOURS_DIRECTION_REQUEST',
	FACILITY_HOURS_DIRECTION_SUCCESS: 'SFDC/FACILITIES/FACILITY_HOURS_DIRECTION_SUCCESS',
	FACILITY_HOURS_DIRECTION_FAILURE: 'SFDC/FACILITIES/FACILITY_HOURS_DIRECTION_FAILURE'
};

export const initialState = {
	loading: false,
	error: false,
	errorMessage: null,
	faq: [],
	byId: {},
	drillDownLinks: null
};

const FACILITY_SUCCESS_TYPES = [
	FACILITIES.SUCCESS,
	FACILITIES.FACILITY_PAGE_SUCCESS,
	SEARCH.SUCCESS,
	SEARCH.SEARCH_LANDING_SUCCESS,
	SEARCH.STORAGE_DEALS_SUCCESS,
	SEARCH.PAGE_SUCCESS,
	PAGES.BOOKING_PAGE_SUCCESS,
	PAGES.MOVE_IN_CONFIRM_STATUS_PAGE_SUCCESS,
	BOOKINGS.RECENT_BOOKING_SUCCESS,
	BOOKINGS.FROM_CONFIRMATION_CODE_SUCCESS,
	UNITS.LOOKUP_SUCCESS,
	REVIEWS.GET_REVIEW_FACILITY_SUCCESS,
	PAGES.CAR_PSP_PAGE_SUCCESS,
	PAGES.STORAGE_PAGE_SUCCESS,
	PAGES.BOAT_PSP_PAGE_SUCCESS,
	PAGES.RV_PSP_PAGE_SUCCESS,
	PAGES.UNIT_PAGE_SUCCESS,
	PAGES.MOVING_TRUCK_PAGE_SUCCESS,
	PAGES.MOVING_TRUCK_CLP_SUCCESS,
];

// TODO: move
export const isInactiveFacilityError = (error) => (
	error &&
		error.message === 'No Facility Available' &&
		error.data.context.reason === 'inactive'
);

export function removeFacilityServiceAreas(facilities) {
	return reduce(
		facilities,
		(allFacilities, facility, key) => ({ ...allFacilities, [key]: omit(facility, ['serviceArea']) }),
		{}
	);
}

const facilitiesSlice = createSlice({
	name: 'facilities',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(FACILITIES.FACILITY_PAGE_FAILURE, (state, { error }) => {
				if (isInactiveFacilityError(error)) {
					const context = get(error, 'data.context', {});
					return {
						...state,
						byId: {
							...state.byId,
							[context.facilityId]: {
								isInactive: true,
								location: context.location
							}
						}
					};
				}
				return state;
			})
			.addCase(REVIEWS.BEST_REVIEWS_SUCCESS, (state, action) => ({
				...state,
				byId: {
					...state.byId,
					...reduce(action.payload.entities.reviews, (updatedFacilities, review) => ({
						...updatedFacilities,
						[review.facilityId]: {
							...state.byId[review.facilityId],
							bestReviewId: review.id
						}
					}), {})
				}
			}))
			.addMatcher(
				(action) => FACILITY_SUCCESS_TYPES.includes(action.type),
				(state, action) => {
					if (has(action, 'payload.entities.facilities')) {
						let facilities = get(action, 'payload.entities.facilities');
						const result = {
							...state,
							faq: get(action, 'payload.result.faq', []),
							drillDownLinks: get(action, 'payload.result.drillDownLinks', null),
						};

						if (get(action, 'payload.result.search')) {
							const search = action.payload.entities.search[action.payload.result.search];
							facilities = removeFacilityServiceAreas(action.payload.entities.facilities);
							const storageCities = get(action, 'payload.result.storageCities');

							if (storageCities && search.searchType === 'storage-only') {
								result.storageCities = storageCities;
							}
						}
						result.byId = mergeFacilities(state.byId, facilities);
						return result;
					}
					return state;
				}
			);
	},
});

// CALL_API action creators (use middleware, cannot be slice reducers)
export function getFacilityByEmailToken(token) {
	return {
		[API]: {
			types: [
				FACILITIES.BY_EMAIL_TOKEN_REQUEST,
				FACILITIES.BY_EMAIL_TOKEN_SUCCESS,
				FACILITIES.BY_EMAIL_TOKEN_FAILURE
			],
			endpoint: `/facility/emailToken/${token}`,
			schema: { facility: facilitySchema },
			adapter: (payload, state) => ({
				...payload,
				facility: payload.facility && formatFacility(payload.facility, state)
			})
		}
	};
}

export function getFacilityPage(facilityId, city, state) {
	return {
		[API]: {
			types: [
				FACILITIES.FACILITY_PAGE_REQUEST,
				FACILITIES.FACILITY_PAGE_SUCCESS,
				FACILITIES.FACILITY_PAGE_FAILURE
			],
			endpoint: `/page/facility/${facilityId}`,
			adapter: formatFacilityResponse,
			schema: { facility: facilitySchema, units: [unitSchema] },
			meta: { city, state }
		}
	};
}

export function getFacilityHoursAndDirection(data) {
	return {
		[API]: {
			method: 'POST',
			types: [
				FACILITIES.FACILITY_HOURS_DIRECTION_REQUEST,
				FACILITIES.FACILITY_HOURS_DIRECTION_SUCCESS,
				FACILITIES.FACILITY_HOURS_DIRECTION_FAILURE
			],
			endpoint: '/consumer/hours-and-directions',
			data
		}
	};
}

export default facilitiesSlice.reducer;
